<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import SmartUnreadBadge from 'dashboard/components-next/Conversation/SmartUnreadBadge.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

const lastNonActivityMessageContent = computed(() => {
  const { lastNonActivityMessage = {}, customAttributes = {} } =
    props.conversation;
  const { email: { subject } = {} } = customAttributes;

  // Check for attachments if no content
  const message = lastNonActivityMessage;
  if (!subject && !message?.content && message?.attachments?.length > 0) {
    const fileType = message.attachments[0]?.file_type || 'file';
    return (
      t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`) ||
      t('CHAT_LIST.ATTACHMENTS.file.CONTENT')
    );
  }

  return getPlainText(
    subject || lastNonActivityMessage?.content || t('CHAT_LIST.NO_CONTENT')
  );
});

const assignee = computed(() => {
  const { meta: { assignee: agent = {} } = {} } = props.conversation;
  return {
    name: agent.name ?? agent.availableName,
    thumbnail: agent.thumbnail,
    status: agent.availabilityStatus,
  };
});

const unreadMessagesCount = computed(() => {
  const { unreadCount } = props.conversation;
  return unreadCount;
});
</script>

<template>
  <div class="wlab-message-preview flex items-end w-full gap-2 pb-1">
    <p class="w-full mb-0 text-sm leading-7 text-n-slate-12 line-clamp-2">
      {{ lastNonActivityMessageContent }}
    </p>
    <div class="flex items-center flex-shrink-0 gap-2 pb-2">
      <Avatar
        v-if="assignee.name"
        :name="assignee.name"
        :src="assignee.thumbnail"
        :size="20"
        :status="assignee.status"
        rounded-full
      />
      <SmartUnreadBadge :count="unreadMessagesCount" />
    </div>
  </div>
</template>

<style scoped>
/* Scalable font sizes for message preview */
.wlab-message-preview p {
  font-size: var(--wlab-font-base) !important;
  line-height: 1.5 !important;
}
</style>
